import {DndContext, type DragEndEvent, closestCenter} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {useMemo, useState} from 'react';
import {PROGRESS_RESULT_VALUES} from '../../../../../../core/entities/entryManagements/constants';
import type {ProgressResultType} from '../../../../../../core/entities/entryManagements/schema';
import type {GetEntryManagementResponse} from '../../../../../../core/repositories/mypageEntryManagementsRepository';
import {OVERALL_STATUS} from '../../../../constants';
import {AddStepButton} from './AddStepButton';
import {AddStepFromReportLink} from './AddStepFromReportLink';
import {CompletedStepsToggleButton} from './CompletedStepsToggleButton';
import {DetailTitleSection} from './DetailTitleSection';
import {SortableStepItem} from './SortableStepItem';

export type StepData = {
  stepId: number;
  stepOrder: number;
  stepName: string;
  deadline: string;
  result: ProgressResultType;
  notes: string;
  startDate?: string;
  endDate?: string;
  place?: string;
  entryTitle?: string | null;
  entrySourceUrl?: string | null;
};

type ReorderedStep = NonNullable<GetEntryManagementResponse['steps']>[number];

type Props = {
  /** エントリー管理詳細データ */
  entryDetail?: GetEntryManagementResponse;
  /** 並び替え済みステップ配列 */
  reorderedSteps: ReorderedStep[];
  /** 最新作成ステップID（AI ToDo生成ポップオーバー表示用） */
  latestCreatedStepId: number | null;
  /** 選考体験記の有無 */
  hasReports: boolean;
  /** AI選考対策データの有無 */
  hasAiReportSummary: boolean;
  /** ステップ追加ハンドラー */
  addStepHandler?: () => void;
  /** ステップ編集ハンドラー */
  editStepHandler?: (stepData: StepData) => void;
  /** 選考結果変更ハンドラー */
  resultChangeHandler?: (stepId: number, result: string) => void;
  /** ステップフィールド更新ハンドラー */
  stepFieldUpdateHandler?: (
    stepId: number,
    field:
      | 'stepName'
      | 'deadline'
      | 'startDate'
      | 'endDate'
      | 'place'
      | 'notes',
    value: string | undefined,
  ) => void;
  /** ドラッグ終了ハンドラー */
  onDragEnd: (event: DragEndEvent) => void;
  /** 選考管理削除ハンドラー */
  onDeleteClick: () => void;
  /** 辞退ステータスに変更 */
  onChangeToArchiveStatus?: () => void;
  /** 内定獲得ステータスに変更 */
  onChangeToCompleteStatus?: () => void;
  /** 落選ステータスに変更 */
  onChangeToRejectedStatus?: () => void;
  /** 未エントリー状態に変更（hasCompletedEntry: false + overallStatus: active） */
  onChangeHasCompletedEntryFalse?: () => void;
  /** エントリー済み状態に変更（hasCompletedEntry: true + overallStatus: active） */
  onChangeHasCompletedEntryTrue?: () => void;
  /** AI ToDo生成ハンドラー */
  onGenerateTodos?: (stepId: number) => void;
  /** AI ToDo生成中のステップID */
  generatingStepId?: number | null;
  /** 体験記分析モーダルを開くハンドラー */
  onOpenReportAnalyzeModal?: () => void;
  /** ステップ削除ハンドラー */
  onDeleteStep?: (stepId: number) => void;
  /** 企業名が編集可能かどうか */
  companyNameEditable?: boolean;
  /** 企業名保存ハンドラー。成功時はtrue、失敗時はfalseを返す */
  onCompanyNameSave?: (
    companyName: string,
    companyId?: number,
  ) => Promise<boolean>;
  /** 企業リスト（Autocomplete用） */
  companyList?: {id: number; value: string}[];
  /** エントリー管理に紐づくToDoの有無判定関数 */
  hasTodosForEntryManagement?: (entryManagementId: number) => boolean;
  /** SP表示かどうか */
  isSp?: boolean;
  /** ステップを1つ上に移動 */
  onMoveUp?: (stepId: number) => void;
  /** ステップを1つ下に移動 */
  onMoveDown?: (stepId: number) => void;
};

/**
 * 完了済み選考予定（非表示対象）の判定
 * 「結果待ち」または「未設定（null）」以外の結果を持つステップを完了済みと判定する
 * @param result - 選考結果（合格/不合格/辞退など）
 * @returns 完了済みステップかどうか
 */
const isCompletedStep = (result: ProgressResultType): boolean => {
  return result !== null && result !== PROGRESS_RESULT_VALUES.pending;
};

export const ProgressDetailPresenter: React.FC<Props> = ({
  entryDetail,
  reorderedSteps,
  latestCreatedStepId,
  hasReports,
  hasAiReportSummary,
  addStepHandler,
  resultChangeHandler,
  stepFieldUpdateHandler,
  onDragEnd,
  onDeleteClick,
  onChangeToArchiveStatus,
  onChangeToCompleteStatus,
  onChangeToRejectedStatus,
  onChangeHasCompletedEntryFalse,
  onChangeHasCompletedEntryTrue,
  onGenerateTodos,
  generatingStepId,
  onOpenReportAnalyzeModal,
  onDeleteStep,
  companyNameEditable = false,
  onCompanyNameSave,
  companyList = [],
  hasTodosForEntryManagement,
  isSp = false,
  onMoveUp,
  onMoveDown,
}) => {
  const [showCompletedSteps, setShowCompletedSteps] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);

  // 完了済みステップの数を計算
  const completedStepsCount = useMemo(
    () => reorderedSteps.filter((step) => isCompletedStep(step.result)).length,
    [reorderedSteps],
  );

  // 結果未選択のステップが存在するかどうか（トグル状態に関わらず判定）
  const hasIncompleteSteps = useMemo(
    () => reorderedSteps.some((step) => !isCompletedStep(step.result)),
    [reorderedSteps],
  );

  // 表示するステップをフィルタリング
  const visibleSteps = useMemo(() => {
    if (showCompletedSteps) {
      return reorderedSteps;
    }
    return reorderedSteps.filter((step) => !isCompletedStep(step.result));
  }, [reorderedSteps, showCompletedSteps]);

  if (!entryDetail) {
    return (
      <div className={'w-full flex flex-col items-center justify-center p-16'}>
        選考管理の読み込みに失敗しました。
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-16">
        <DetailTitleSection
          companyId={
            entryDetail.companyID !== null && entryDetail.companyID !== 0
              ? entryDetail.companyID
              : undefined
          }
          entryManagementId={entryDetail.id}
          companyName={entryDetail.companyName}
          onDeleteClick={onDeleteClick}
          companyNameEditable={companyNameEditable}
          onCompanyNameSave={onCompanyNameSave}
          companyList={companyList}
          hasReports={hasReports}
          hasAiReportSummary={hasAiReportSummary}
          hasTodosForEntryManagement={hasTodosForEntryManagement}
          overallStatus={entryDetail.overallStatus}
          onChangeToCompleteStatus={onChangeToCompleteStatus}
          onChangeToArchiveStatus={onChangeToArchiveStatus}
          onChangeToRejectedStatus={onChangeToRejectedStatus}
          hasCompletedEntry={entryDetail.hasCompletedEntry ?? false}
          onChangeHasCompletedEntryFalse={onChangeHasCompletedEntryFalse}
          onChangeHasCompletedEntryTrue={onChangeHasCompletedEntryTrue}
          isSp={isSp}
          isReorderMode={isReorderMode}
          onToggleReorderMode={() => setIsReorderMode(!isReorderMode)}
          canReorder={visibleSteps.length >= 2}
        />
      </div>

      <div className="bg-background-1 px-16 rounded-16 h-[calc(100vh-205px)] pc:h-[calc(100vh-155px)] overflow-auto relative">
        {completedStepsCount > 0 && (
          <div className="mt-16 flex justify-center">
            <CompletedStepsToggleButton
              showCompleted={showCompletedSteps}
              completedCount={completedStepsCount}
              onToggle={() => setShowCompletedSteps(!showCompletedSteps)}
            />
          </div>
        )}
        {visibleSteps.length > 0 && (
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={!isSp ? onDragEnd : undefined}
          >
            <SortableContext
              items={visibleSteps.map((step) => step.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col gap-16 pt-16">
                {visibleSteps.map((step, index) => (
                  <SortableStepItem
                    key={step.id}
                    id={step.id}
                    stepId={step.id}
                    stepName={step.stepName}
                    result={step.result}
                    deadline={step.deadline ?? undefined}
                    startDate={step.startDate ?? undefined}
                    endDate={step.endDate ?? undefined}
                    place={step.place ?? undefined}
                    notes={step.notes ?? ''}
                    created={step.created}
                    onResultChange={resultChangeHandler}
                    onStepFieldUpdate={stepFieldUpdateHandler}
                    onGenerateTodos={onGenerateTodos}
                    isGenerating={generatingStepId !== null}
                    shouldShowGeneratePopover={step.id === latestCreatedStepId}
                    onDeleteStep={onDeleteStep}
                    isSp={isSp}
                    isReorderMode={isReorderMode}
                    onMoveUp={onMoveUp}
                    onMoveDown={onMoveDown}
                    isFirst={index === 0}
                    isLast={index === visibleSteps.length - 1}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
        {entryDetail.overallStatus === OVERALL_STATUS.ACTIVE &&
          !hasIncompleteSteps && (
            <p className="pt-16 flex items-center justify-center text-base text-center">
              次の選考予定が未設定です。
              <br />
              選考結果を更新、または次の選考予定を追加してください。
            </p>
          )}

        <div className="sticky bottom-0 left-0 w-full bg-background-1 py-16">
          <div className="flex flex-col items-center gap-8">
            <div className="flex flex-col gap-8 items-center">
              {addStepHandler && (
                <AddStepButton
                  onClick={addStepHandler}
                  disabled={entryDetail.overallStatus !== 'active'}
                />
              )}
            </div>
            {hasAiReportSummary && onOpenReportAnalyzeModal && (
              <AddStepFromReportLink
                onClick={onOpenReportAnalyzeModal}
                disabled={entryDetail.overallStatus !== 'active'}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
